/**
 * The first thing a new organization's administrator sees, and the answer to
 * the question they actually have: what do I still need before I can start?
 *
 * IT SAYS WHAT IS TRUE, WHICH IS THAT ALMOST NOTHING IS REQUIRED. A new
 * StandingDocs arrives with no departments, no document types, no categories,
 * no approval workflow and no numbering configuration, which reads like five
 * prerequisites. It is not: a policy can be written, routed, approved,
 * published and acknowledged with all five still empty, and
 * `freshOrgReadiness.int.test.ts` walks a bare organization through exactly
 * that. The one thing that genuinely blocks is somebody who can approve.
 *
 * So this leads with "You can start now" when that is so, and offers the rest
 * as suggestions with the reason each is worth doing. A checklist that demands
 * work the product does not need teaches the reader that the list is noise.
 *
 * The rows come from the server (`getOrgReadiness`), which counts what the
 * organization has. Nothing here decides what is required; that rule lives
 * once, in `functions/src/admin/orgReadiness.ts`.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { call } from '../../lib/callable';
import { usePersistentChoice } from '../../shared/usePersistentChoice';
import type { Role } from '../../types/roles';
import styles from '../../shared/Page.module.css';

export interface ReadinessItem {
  key: string;
  label: string;
  detail: string;
  done: boolean;
  required: boolean;
  to: string;
  action: string;
}

export interface Readiness {
  items: ReadinessItem[];
  blocking: string[];
  canPublish: boolean;
}

/** The line at the top: what this organization can do right now. */
export function readinessHeadline(r: Readiness): string {
  const left = r.items.filter((i) => !i.done).length;
  if (!r.canPublish) {
    return 'One thing is missing before a policy can be approved and published here.';
  }
  if (left === 0) return 'Everything here is set up.';
  return 'You can write, approve and publish a policy right now; nothing below has to be done first. '
    + `${left} ${left === 1 ? 'suggestion' : 'suggestions'} worth a look when you have a moment.`;
}

interface Props { orgId: string; roles: readonly Role[] }

export function GettingStarted({ orgId, roles }: Props) {
  const mayConfigure = roles.includes('admin') || roles.includes('sysadmin');
  const readiness = useQuery({
    queryKey: ['org-readiness', orgId],
    enabled: mayConfigure,
    retry: false,
    staleTime: 60_000,
    queryFn: () => call<Record<string, never>, Readiness>('getOrgReadiness', {}),
  });
  const [state, setState] = usePersistentChoice(`gettingStarted:${orgId}`, ['open', 'dismissed'] as const, 'open');

  if (!mayConfigure || state === 'dismissed' || !readiness.data) return null;
  const r = readiness.data;
  const rows = r.items;
  const remaining = rows.filter((i) => !i.done).length;
  if (remaining === 0) return null;

  return (
    <section className={styles.section} aria-labelledby="getting-started-title">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
        <h2 className={styles.sectionTitle} id="getting-started-title">Setting up</h2>
        <button type="button" className={styles.btn} onClick={() => setState('dismissed')}>Hide this</button>
      </div>
      <p className={styles.itemMeta} style={{ marginBottom: 'var(--sp-3)', maxWidth: '78ch' }}>
        {readinessHeadline(r)}
      </p>
      <ul className={styles.list} aria-label="Setting up">
        {rows.map((row) => (
          <li key={row.key} className={styles.listItem}>
            <div>
              <div className={styles.itemName}>
                <span aria-hidden="true" style={{ marginRight: 8 }}>{row.done ? '✓' : '○'}</span>
                <span className="vh">{row.done ? 'Done: ' : row.required ? 'Needed: ' : 'Suggested: '}</span>
                {row.label}
                {row.required && !row.done && (
                  <span className={styles.itemMeta} style={{ marginLeft: 8, fontWeight: 600 }}>Needed first</span>
                )}
              </div>
              <div className={styles.itemMeta} style={{ maxWidth: '78ch' }}>{row.detail}</div>
            </div>
            {!row.done && <Link to={row.to} className={styles.btn}>{row.action}</Link>}
          </li>
        ))}
      </ul>
    </section>
  );
}
